// ─────────────────────────────────────────────────────────────
// OVERRIDES D'IMAGES — remplacements uploadés par l'admin.
//
// Un « override » remplace l'image ET le calage d'UN objet du
// catalogue, sans toucher au code. Le moteur les traite comme une
// tuile Kenney normale : même drawPos(), aucune règle de rendu à part.
//
// Champs d'un override :
//   url       PNG normalisé sur le gabarit du pack (256×352), data URL
//   anchorY   hauteur de calage, en pixels de l'image
//   isCube    true = remplace le sol ; false = se pose dessus
//   scale     agrandissement de l'image. 1 pour un bloc d'une case ;
//             pour une structure de plusieurs cases remplacée par UN
//             seul dessin, vaut la largeur de l'emprise (2, 3…).
//   bbox      [x0,y0,x1,y1] : boîte englobante réelle du sujet dans
//             l'image, mesurée sur le canal alpha au moment de
//             l'upload. C'est elle qui permet aux vignettes de la
//             boutique et de l'éditeur de cadrer l'image EXACTEMENT
//             comme une tuile Kenney — même point, même taille.
//             Sans elle, une image dont le sujet est posé bas dans
//             son gabarit apparaissait rapetissée et collée en bas.
// ─────────────────────────────────────────────────────────────

import api from "@/lib/api";
import { IMG_W, IMG_H } from "./tileset.js";

// On passe par le même client que le reste de l'app, pas par fetch()
// avec un chemin relatif : frontend et backend vivent sur deux origines
// différentes, un fetch relatif tape sur le frontend lui-même.
let OVERRIDES = {};
const imgCache = new Map();
const bboxCache = new Map();
let loaded = false;

export async function loadOverrides() {
  try {
    // paramètre anti-cache : jamais de réponse plus vieille que le
    // dernier enregistrement de l'admin.
    const r = await api.get(`/oasis/image-overrides?t=${Date.now()}`);
    OVERRIDES = r.data || {};
  } catch {
    OVERRIDES = {};
  }
  loaded = true;
  return OVERRIDES;
}

export function overridesReady() {
  return loaded;
}

export function overrideFor(itemId) {
  return OVERRIDES[itemId] || null;
}

export function overrideImage(itemId) {
  if (imgCache.has(itemId)) return imgCache.get(itemId);
  const ov = OVERRIDES[itemId];
  if (!ov) return null;
  const img = new Image();
  img.src = ov.url;
  imgCache.set(itemId, img);
  return img;
}

// Enregistre un override côté client sans passer par le serveur :
// utilisé par les objets créés par l'admin (customItems.js), dont
// l'image fait partie de la définition de l'objet lui-même.
export function registerOverride(itemId, ov) {
  OVERRIDES[itemId] = ov;
  imgCache.delete(itemId);
}

export function invalidateOverride(itemId) {
  imgCache.delete(itemId);
}

export async function refreshOverrides() {
  const prev = OVERRIDES;
  await loadOverrides();
  for (const id of Object.keys({ ...prev, ...OVERRIDES })) {
    if (JSON.stringify(prev[id]) !== JSON.stringify(OVERRIDES[id])) imgCache.delete(id);
  }
}

// ── Boîte englobante ──────────────────────────────────────────
// Mesure les bords non transparents d'une image. Appelée à l'upload
// (résultat enregistré avec l'override), et en secours pour les
// overrides enregistrés avant l'ajout de ce champ.
export function measureBBox(source) {
  return new Promise((resolve) => {
    const done = (bb) => resolve(bb);
    const img = source instanceof Image ? source : new Image();
    const run = () => {
      try {
        const c = document.createElement("canvas");
        c.width = IMG_W; c.height = IMG_H;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, IMG_W, IMG_H);
        const { data } = ctx.getImageData(0, 0, IMG_W, IMG_H);
        let x0 = IMG_W, y0 = IMG_H, x1 = 0, y1 = 0, found = false;
        for (let y = 0; y < IMG_H; y++) {
          for (let x = 0; x < IMG_W; x++) {
            if (data[(y * IMG_W + x) * 4 + 3] > 12) {
              found = true;
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        done(found ? [x0, y0, x1 + 1, y1 + 1] : [0, 0, IMG_W, IMG_H]);
      } catch {
        done([0, 0, IMG_W, IMG_H]); // canvas « taché » : on ne bloque pas
      }
    };
    if (img.complete && img.naturalWidth) return run();
    img.onload = run;
    img.onerror = () => done([0, 0, IMG_W, IMG_H]);
    if (!(source instanceof Image)) img.src = source;
  });
}

// Version synchrone avec cache : renvoie la bbox si elle est connue,
// sinon lance la mesure et renvoie null (l'appelant réessaiera au
// rendu suivant, via le callback).
export function bboxOf(itemId, onReady) {
  const ov = OVERRIDES[itemId];
  if (!ov) return null;
  if (ov.bbox && ov.bbox.length === 4) return ov.bbox;
  if (bboxCache.has(itemId)) return bboxCache.get(itemId);
  bboxCache.set(itemId, null); // évite les mesures en rafale
  measureBBox(ov.url).then((bb) => {
    bboxCache.set(itemId, bb);
    onReady?.(bb);
  });
  return null;
}
